import React from 'react';
import { BoxSizeOptions, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import { View } from 'react-native';
import { BodyText, Touchable } from '../../../ui';
import { useLinkTo, useParams } from '../../../../hooks';
import RelatedSalons from '../../../../modules/consumer/search/RelatedSalons';
import { toSlugCase } from '../../../../modules/stringUtils';
import { stateHref } from '../../../../store/RouteState.model';
import { PublicProvider } from '../../../../api/Providers';
import { ClientProviderProfile } from '../../../../api/ProfileClientView';
import { getProfileLocation } from '../../../../api/ProfileClientView/ProfileClientView';
import { CITY_SEARCH_LANDING, SEARCH_ROUTE } from '../../../../route-names';

const handleBreadCrumbClick = (breadCrumb, linkTo) => linkTo(breadCrumb.route, breadCrumb.params);

const getBreadCrumbs = (
  provider: PublicProvider | ClientProviderProfile,
  routeParams: Record<string, string>,
) => {
  const relatedService = RelatedSalons.getRelatedServiceForProfession(provider.profession);
  const location = getProfileLocation(provider);
  const city: string = location?.city;
  const state: string = location?.state;
  let breadcrumbs = [];
  if (city && state) {
    breadcrumbs = [
      {
        text: `${city}, ${state}`,
        route: CITY_SEARCH_LANDING,
        params: { loc: `${toSlugCase(city)}-${toSlugCase(state)}` },
      },
    ];
  }
  if (relatedService && city && state) {
    breadcrumbs = [
      ...breadcrumbs,
      {
        text: relatedService,
        route: SEARCH_ROUTE,
        params: { q: toSlugCase(relatedService), loc: `${toSlugCase(city)}-${toSlugCase(state)}` },
      },
    ];
  }
  if (provider.id) {
    breadcrumbs = [
      ...breadcrumbs,
      {
        text: provider.name,
        route: 'profile',
        params: {
          providerId: Number.isNaN(Number(routeParams?.providerId))
            ? provider.vanity_url
            : provider.id,
        },
      },
    ];
  }

  return breadcrumbs;
};

export const ServiceListBreadCrumbs = (
  {
    provider,
    paddingTop = 16,
  }: {
    provider: PublicProvider | ClientProviderProfile;
    paddingTop?: BoxSizeOptions;
  },
) => {
  const routeParams = useParams();
  const breadCrumbs = getBreadCrumbs(provider, routeParams);
  const linkTo = useLinkTo();

  return (
    <Inline
      paddingTop={paddingTop}
      alignItems="center"
      justifyContent="center"
      testID="serviceListBreadCrumbsContainer"
    >
      {breadCrumbs.map((breadCrumb, index) => (
        <Touchable
          testID={`serviceListBreadCrumb-${index}`}
          onPress={() => handleBreadCrumbClick(breadCrumb, linkTo)}
          key={breadCrumb.text}
        >
          <View
            {...{ href: stateHref(breadCrumb.route, breadCrumb.params) }}
          >
            <BodyText appearance="small" weight="thin" color={BrandColor.Metal}>
              {breadCrumb.text} {index < breadCrumbs.length - 1 ? ' / ' : ''}
            </BodyText>
          </View>
        </Touchable>
      ))}
    </Inline>
  );
};
